/**
 * OBW trust-assessment & journey-resolution — ADR-077.
 *
 * The OBW engine assesses its REAL runtime context at entry and resolves its
 * path from what it finds. It is context-INDEPENDENT precisely because context
 * is NOT wired in — it is ASSESSED, not assumed (ADR-077 §2). This module is the
 * pure, side-effect-free heart of that assessment: a mount supplies the assessed
 * signals ({authenticated, actor, accountState}); these functions decide.
 *
 * Two responsibilities, each a distinct decision (ADR-077 §2.2 — descending
 * trust gradient: security-decisive questions first, flow questions last):
 *
 *   assertTrust(journeyId, {authenticated, actor})
 *     ENFORCEMENT (§2.3). The engine's intrinsic, unskippable entry gate. Given a
 *     journey's required trust (derived from JOURNEY_MATRIX.allowedActors — the
 *     matrix-as-config survives ADR-021, now an INPUT to assessment), it verifies
 *     the entry's trust level is sufficient. Insufficient → refusal, NEVER a
 *     silent privileged run. 1st gate = isAuthenticated (the trust boundary);
 *     2nd = isNexaAdmin (actor/privilege — admin is a higher tier than subscriber
 *     and may run subscriber journeys too).
 *
 *   resolveSubscriberEntry(ctx, {hasPlanParam})
 *     RESOLUTION (§3). Reads the REAL account state — never infers a journey from
 *     the presence/absence of a URL param (that inference IS the BOW bug, §1.2).
 *     A provisioned account (beta, card-first SOW, or otherwise set-up) lands on
 *     the DASHBOARD, not a wizard; the snippet arrives by email (ADR-076 §5). The
 *     requiredAction gate still catches genuinely-incomplete PAID setup.
 *
 * Severance is preserved (ADR-076 §4): this module reads no auth/session/route/
 * portal API itself — the mount assesses the live context and passes the signals
 * in. Wiring-in (presuming context) is the opposite of assessing (detecting +
 * verifying it) — ADR-077 §2.4.
 */
import { JOURNEY_MATRIX, type JourneyId, type ObwStepId } from './journeys';
import { firstIncompleteRequiredStep, type ObwEvidence } from './validators';

export type Actor = 'subscriber' | 'nexa-admin';

/**
 * setupStates that mean "this account's setup is FINISHED — land on the
 * dashboard". A card-first SOW activation rests at `paid_pending_activation`
 * (paid stub, activated for a session — ADR-073 §2.3); a fully set-up account at
 * `complete` / `active`.
 *
 * ⚠ READ ONLY IN THE "FINISHED" DIRECTION (ADR-107 §2.3 — see the branch that
 * consumes it). This set can send someone to their dashboard; nothing derived
 * from `setupState` may send anyone into a wizard.
 */
const SETUP_DONE = new Set<string>(['complete', 'active', 'paid_pending_activation']);

/** User-facing refusal copy — surfaced via the shared toast by the mount (L2). */
export const INSUFFICIENT_TRUST_MESSAGE =
  "You don't have permission to do this.";

/**
 * The real account record's state, read by the mount and passed in. Every field
 * is optional so a partial / still-loading read degrades safely (resolution
 * treats unknowns conservatively).
 */
export interface AccountState {
  /**
   * Account record setupState.
   *
   * ⛔ DEMOTED (ADR-107 §2.3) — a coarse LIFECYCLE marker, never a progress
   * signal. `signup-api` stamps it at account CREATION, so it reads
   * `awaiting_payment` for a subscriber who has run no step. It reaches the
   * validators only as corroboration for `payment` (PAST_PAYMENT_STATES, which
   * deliberately excludes `awaiting_payment` for exactly this reason).
   */
  setupState?: string | null;
  /** `Account.tier` — server-side evidence for the `subscribe` validator. */
  tier?: string | null;
  /**
   * How many Domain rows the account owns — SERVER TRUTH for the `domain-verify`
   * validator, and the field whose absence let an account finish setup owning
   * nothing. /portal/me already computed this for the ACCOUNT/GROUP navbar badge;
   * ADR-107 surfaces it.
   */
  domainCount?: number;
  /** ADR-073 Am.2 A2.5 beta flag — a provisioned beta account never runs a wizard. */
  isBeta?: boolean;
  /** True once the Account record exists server-side (the user has an account). */
  provisioned?: boolean;
  /** The /portal/me requiredAction gate — genuinely-incomplete PAID setup only. */
  requiredAction?: {
    type: 'complete_payment' | 'select_plan_and_pay' | 'resume_setup';
    tier?: string;
  } | null;
  /**
   * #3 (resume restoration) — the LAST COMPLETED OBW step, persisted by
   * `handleObwStep` on every step transition and now read back by /portal/me.
   *
   * ⚠ It is the step the user FINISHED, not the step they were standing on:
   * the engine posts `currentStepId` as it advances. Resuming therefore means
   * opening the step AFTER this one — `resolveResumeStep` below owns that
   * off-by-one so no mount has to remember it.
   */
  lastObwStep?: string | null;
  /**
   * The ANSWERS already given, keyed by stepId — the other half of a resume.
   *
   * ⚠ THE STEP POINTER ALONE IS NOT A RESUME. Shipped with only `lastObwStep`,
   * re-entry reopened the correct step over an EMPTY data map — so `StepPayment`
   * (which reads `data.selectedTier`) told a subscriber who had already chosen a
   * plan to "go back and choose a plan to continue". Reopening a step whose
   * prerequisites have silently vanished is worse than restarting: it looks like
   * the answers were lost, because they were.
   */
  obwStepData?: Record<string, Record<string, unknown>> | null;
  /** The journey those answers were given in — see `resolveResumeStepData`. */
  obwJourneyId?: string | null;
}

/**
 * #4 — resolve the ANSWER seed for a resumed journey, or undefined for none.
 *
 * ⭐ JOURNEY-MATCHED, AND THAT GUARD IS LOAD-BEARING. Step ids are SHARED across
 * journeys — `subscribe`, `payment`, `brand` and `profile` all recur in
 * `add-second-domain`, `subscribe-existing-domain` and `b2s`. Seeding blind
 * would pre-fill an unrelated journey with an abandoned sign-up's answers, and
 * the user would never see where they came from: a plan they did not pick this
 * time, silently selected. A stored map from a different journey is therefore
 * IGNORED WHOLE, never merged step-by-step — a partial application is the worst
 * of both (some fields theirs, some stale, no way to tell which).
 *
 * An absent journey id (an account mid-journey from before this shipped) is
 * likewise ignored: unknown provenance is not a match.
 */
export function resolveResumeStepData(
  journeyId: JourneyId,
  accountState: AccountState | null | undefined,
): Record<string, Record<string, unknown>> | undefined {
  const stored = accountState?.obwStepData;
  if (!stored || typeof stored !== 'object') return undefined;
  if (!accountState?.obwJourneyId || accountState.obwJourneyId !== journeyId) return undefined;
  return Object.keys(stored).length > 0 ? stored : undefined;
}

/**
 * #3 — resolve the step a re-entering journey should OPEN at, from the last
 * step the user COMPLETED.
 *
 * The engine had no resume at all (`useState(0)`), so every re-entry — this
 * gate, a future "Continue setup" CTA, the admin onboard mounts — restarted a
 * part-finished subscriber at Step 1 of 8. That is a genuine bug independent of
 * any billing provider, which is why the seeding lives here (pure, testable)
 * rather than inside a mount.
 *
 * Contract:
 *   • no/unknown last step → undefined (start at the beginning — a fresh entry)
 *   • a step not in THIS journey's effective sequence → undefined (a journey
 *     switch, or a step the mount elided via skipSteps; never index blindly)
 *   • the FINAL step completed → clamp to the final step, never wrap to 0. Such
 *     an account should have been resolved to its dashboard before reaching a
 *     mount; if one slips through, holding the end is the harmless outcome and
 *     restarting the wizard is the reported defect.
 */
export function resolveResumeStep<T extends string>(
  steps: readonly T[],
  lastCompletedStepId: string | null | undefined,
): T | undefined {
  if (!lastCompletedStepId) return undefined;
  const i = steps.indexOf(lastCompletedStepId as T);
  if (i < 0) return undefined;
  return steps[Math.min(i + 1, steps.length - 1)];
}

export interface TrustContext {
  authenticated: boolean;
  actor: Actor;
  accountState?: AccountState | null;
  /**
   * ⭐⭐ ADR-116 D-A5 — the personal roles this identity holds, from /portal/me's
   * `personalRoles`. Consultant and Advocate are *"individual, personal roles —
   * there is no account or group concept for either"*, so an identity holding
   * one has no Account record and never will.
   *
   * ⛔ IT IS ON `TrustContext`, NOT ON `AccountState`, AND THAT PLACEMENT IS THE
   * ARGUMENT. `AccountState` describes an ACCOUNT; this describes a PERSON, and
   * the entire point is that these people have no account for it to hang off.
   * Putting it there would have made the guard read a field of a record that
   * does not exist — the same category error the guard exists to stop.
   */
  personalRoles?: readonly string[];
}

/**
 * ⭐⭐ THE CLASS THE GUARD IS WRITTEN AGAINST — mirrors `PERSONAL_ROLES` in
 * `lambdas/account/src/personal-roles.ts`, which carries the full reasoning.
 *
 * ⚠ THE GUARD DOES NOT READ THIS SET, AND THAT IS DELIBERATE. It fires on ANY
 * non-empty `personalRoles`, so a role the server starts sending before this
 * copy is updated is still kept out of the wizard. The set is used only to name
 * a HOME for routing — the half that can safely fail soft. Keeping the safety
 * half open-ended and the routing half explicit is what makes a new role of this
 * shape covered by construction rather than by remembering to edit two files.
 */
export const KNOWN_PERSONAL_ROLES = ['consultant', 'advocate'] as const;
export type PersonalRole = (typeof KNOWN_PERSONAL_ROLES)[number];

export interface TrustVerdict {
  ok: boolean;
  /** Present only when ok === false. */
  message?: string;
}

/**
 * ENFORCEMENT (ADR-077 §2.3). Is the entry's assessed trust sufficient to run
 * this journey? The engine calls this as its first action and refuses if not.
 *
 * Trust gradient (§2.2):
 *   1st — isAuthenticated: the trust boundary. An unauthenticated entry is
 *         refused outright — with ONE exception, built by ADR-073 Am.5 §A5.6
 *         (5 October 2026): the FEP's PUBLIC mount, whose trust is established BY
 *         PAYMENT (ADR-080 §3, `SowFepContext.trust = { via: 'payment' }`). The FEP is
 *         the SOW's trust tier; the engine accepts its verdict rather than
 *         re-deriving it. That mount supplies no authenticated port before payment —
 *         only the lead, through the edge-gated public routes — so nothing privileged
 *         is reachable from it until the payment confirm, which is the trust event.
 *         It still needs a journey that admits the `subscriber` actor (2nd gate).
 *   2nd — isNexaAdmin (actor): a higher trust tier within authenticated. An
 *         admin actor may run admin-assisted journeys AND subscriber journeys
 *         (acting on behalf). A subscriber actor may run only journeys whose
 *         allowedActors include 'subscriber'; attempting an admin-only journey
 *         (e.g. bow, manual-onboard-*) is refused.
 */
export function assertTrust(
  journeyId: JourneyId,
  ctx: { authenticated: boolean; actor: Actor; fepTrust?: 'payment' },
): TrustVerdict {
  // 1st gate — the trust boundary (or the FEP public mount's trust-by-payment — see above).
  const publicByPayment = ctx.fepTrust === 'payment' && ctx.actor === 'subscriber';
  if (!ctx.authenticated && !publicByPayment) {
    return { ok: false, message: INSUFFICIENT_TRUST_MESSAGE };
  }
  const journey = JOURNEY_MATRIX[journeyId];
  // 2nd gate — actor/privilege. nexa-admin is the higher tier and clears any
  // journey; a subscriber clears only journeys that admit subscribers.
  if (ctx.actor === 'nexa-admin') {
    return { ok: true };
  }
  if (journey.allowedActors.includes('subscriber')) {
    return { ok: true };
  }
  return { ok: false, message: INSUFFICIENT_TRUST_MESSAGE };
}

export type Resolution =
  /** Not authenticated — the mount sends them to sign-in. */
  | { kind: 'sign-in' }
  /** A provisioned account with every required validator passing; no wizard. */
  | { kind: 'dashboard'; reason: 'beta' | 'setup-complete' | 'provisioned' | 'state-unknown' }
  /**
   * ⭐⭐ ADR-116 D-A5 — a personal, non-account identity. NOT `dashboard`, and the
   * distinction is load-bearing: `dashboard` means `/dash/:accountId`, and these
   * people have no accountId to put in it. Sending them there would trade a wrong
   * wizard for a broken URL.
   *
   * `role` is the FIRST held role, which is also the only one in practice —
   * D-A1/D-A2 make consultant and advocate mutually exclusive with everything
   * except each other, and D-A5 excludes account membership from both.
   *
   * ⚠ A ROLE WITH NO HOME BUILT YET IS STILL A VALID RESOLUTION. Advocate has no
   * dashboard; the mount shows a holding state rather than routing. "Nowhere to
   * send them yet" is not a reason to send them somewhere wrong.
   */
  | { kind: 'personal-role-home'; role: string }
  /**
   * Run an OBW journey — and OPEN AT `openAt` (ADR-107 §2.2).
   *
   * ⭐ `openAt` REPLACES THREE MECHANISMS AT ONCE. Resume was a `lastObwStep`
   * cursor, entry was a `setupState`-derived `requiredAction` redirect, and
   * completion was an index comparison. Three mechanisms that could disagree —
   * and a "loop" is precisely two mechanisms each correctly undoing the other.
   * All three are now the first required step whose validator fails, which is one
   * value that cannot disagree with itself.
   *
   * undefined ⇒ start at the beginning (a fresh entry with no evidence at all).
   */
  | { kind: 'journey'; journeyId: JourneyId; openAt?: ObwStepId };

/**
 * Build the evidence bundle from an assessed AccountState (ADR-107 §2.1).
 *
 * Kept here rather than in the mount so every consumer — OnboardPage, the
 * dashboard gate, the engine's own Done-check — derives evidence identically
 * from the same read.
 */
export function evidenceFromAccountState(
  acct: AccountState | null | undefined,
  sessionAnswers?: Partial<Record<ObwStepId, Record<string, unknown>>>,
): ObwEvidence {
  const a = acct ?? {};
  return {
    answers: {
      ...((a.obwStepData ?? {}) as Partial<Record<ObwStepId, Record<string, unknown>>>),
      ...(sessionAnswers ?? {}),
    },
    tier: a.tier ?? null,
    setupState: a.setupState ?? null,
    domainCount: a.domainCount ?? 0,
    provisioned: !!a.provisioned,
  };
}

/**
 * RESOLUTION (ADR-107 §2.2) for the subscriber-portal OBW entry (OnboardPage).
 *
 * ⛔ WHAT WAS REMOVED, AND WHY IT HAD TO GO
 * ─────────────────────────────────────────
 * The old ladder resolved from `setupState` → `requiredAction` → `lastObwStep` →
 * the `?plan=` param, with a special case bolted on for each UAT failure. Every
 * rung was a proxy for "how far has this person got", and every rung was wrong in
 * a different way:
 *
 *   · `setupState` is stamped by `signup-api` AT ACCOUNT CREATION, so it reads
 *     `awaiting_payment` for a subscriber who has run no step at all. That is what
 *     routed a first-time entrant to Payment, past the domain mint, to an account
 *     owning zero Domain rows.
 *   · `requiredAction` is derived from `setupState`, so it inherits the same lie.
 *   · `lastObwStep` was written only by an UNAUTHENTICATED call that 401'd in
 *     production (ADR-107 §1.2), so it was absent for every real subscriber — and
 *     the "absence means never-started" discriminator added in #313 was reading a
 *     field nothing had ever successfully written.
 *   · `?plan=` is sticky debris that outranked reality until #307 subordinated it.
 *
 * The replacement asks the only question that was ever meant: **which required
 * step is not actually done?** Evidence, not flags.
 *
 * Order:
 *   1. Unauthenticated → sign-in (the trust boundary — unchanged).
 *   2. Unknown state → dashboard. Unknown must never fail OPEN into a wizard; see
 *      `accountStateKnown`.
 *   3. Beta → dashboard, always, before anything else can drag it into the SOW.
 *   4. Every required validator passes → dashboard.
 *   5. Otherwise → the sign-up journey, OPENED AT the first failing required step.
 *
 * The `?plan=` param no longer resolves anything. It survives as a step SEED (the
 * mount still passes it as `initialPlan`), which is all it was ever good for: on a
 * fresh entry the `subscribe` validator fails either way, so the journey opens at
 * the same place with the tier pre-filled.
 */
/**
 * ADR-077 A1.3 — POW apply journey entry resolution: the continue/restart fork.
 *
 * Genuinely NON-TERMINAL (in-progress) Brief statuses. The fork is shown ONLY
 * for these — an application the applicant submitted that has not reached a
 * decided outcome. Mirrors portal-api brief.ts IN_PROGRESS_STATUSES. Decided
 * states (approved, active, rejected, superseded, etc.) do NOT trigger the fork:
 * decided-partner statuses are handled by the status page; reapply-allowed
 * statuses just start fresh.
 */
const IN_PROGRESS_APPLICATION_STATUSES = new Set<string>([
  'pending_validation',
  'pending_reassessment',
  'staff_review',
  'pending_response',
  'human_review_required',
]);

export function isInProgressApplicationStatus(status: string | null | undefined): boolean {
  return status != null && IN_PROGRESS_APPLICATION_STATUSES.has(status);
}

export type PartnerApplyEntry =
  /** No in-progress application — run the apply journey as normal. */
  | { kind: 'fresh' }
  /** An in-progress application exists — show the continue/restart fork. */
  | { kind: 'fork'; applicationId: string; status: string };

/**
 * ADR-077 A1.3 — resolve POW apply entry from the account's latest Brief (read
 * by the mount). In-progress → fork (Continue resumes, Start fresh supersedes);
 * anything else → fresh. The engine asserts NO default (the fork itself asks).
 */
export function resolvePartnerApplyEntry(
  latestBrief: { applicationId: string; status: string } | null | undefined,
): PartnerApplyEntry {
  if (latestBrief && isInProgressApplicationStatus(latestBrief.status)) {
    return { kind: 'fork', applicationId: latestBrief.applicationId, status: latestBrief.status };
  }
  return { kind: 'fresh' };
}

export function resolveSubscriberEntry(
  ctx: TrustContext,
  opts: {
    /**
     * ⛔ RETIRED AS A RESOLUTION INPUT (ADR-107 §2.2) — accepted and ignored so
     * existing call sites compile unchanged.
     *
     * `?plan=` never resolved a journey correctly. It is sticky (nothing strips
     * it), so it survived the Back button, a reopened activation email, a
     * bookmark and a dashboard bounce, and each of those re-entries restarted a
     * completed account at step one. #307 subordinated it to `provisioned`;
     * ADR-107 removes it from resolution altogether. It survives where it was
     * always legitimate: as the `subscribe` step's SEED (`initialPlan`), which
     * pre-fills the tier without claiming anything about progress.
     */
    hasPlanParam?: boolean;
    /**
     * Did the account-state read actually SUCCEED? Defaults to true (an explicit
     * signal is required to claim otherwise). See the fail-open note in the body —
     * this is the single most load-bearing input in the function.
     */
    accountStateKnown?: boolean;
  },
): Resolution {
  if (!ctx.authenticated) {
    return { kind: 'sign-in' };
  }

  const acct = ctx.accountState ?? {};

  // UNKNOWN STATE — refuse to resolve anything from a read that did not happen.
  //
  // ⭐ UNKNOWN MUST NOT FAIL OPEN INTO A WIZARD, and it is FIRST for that reason.
  // `getAccount` swallows its own DDB errors and returns null, /portal/me maps that
  // to `provisioned: false`, and a mount defaulting to "not provisioned" on any
  // fetch failure produces the exact byte-for-byte state of a brand-new visitor. A
  // throttle or a cold start at the moment of re-entry would then restart a PAID
  // subscriber's sign-up and ask them to choose a plan again. The two errors are
  // not symmetric: sending a genuinely-new user to the dashboard is a momentary
  // dead end they recover from by re-entering; restarting a paying customer's
  // sign-up is the reported defect. So unknown resolves to the dashboard, which
  // performs its own read rather than acting on this one.
  //
  // ⚠ AND IT MUST STAY AHEAD OF THE VALIDATORS. With no evidence, EVERY validator
  // fails — so a resolver that ran them on an unknown state would confidently
  // report "nothing is done" and open the wizard at step one. That is the same
  // fail-open in a new coat, which is exactly how this defect class keeps
  // returning. Absent evidence is not evidence of absence.
  if (opts.accountStateKnown === false) {
    return { kind: 'dashboard', reason: 'state-unknown' };
  }

  // ⭐⭐ ADR-116 D-A5 — A PERSONAL, NON-ACCOUNT IDENTITY NEVER RUNS A JOURNEY.
  //
  // Positioned exactly like the beta guard below, and for exactly the same
  // reason: ahead of every other branch so nothing underneath can drag one of
  // these identities into the paid journey.
  //
  // ⛔ IT MUST BE AHEAD OF THE VALIDATORS SPECIFICALLY. This function's default
  // for "no Account record" is `journey: sign-up @ domain-verify`, and that
  // default is correct — for a subscriber. A consultant has no domain, no
  // business and nothing to pay for, so EVERY validator fails for them exactly as
  // it fails for a genuinely-new visitor. The two are byte-identical on account
  // state alone; `personalRoles` is the only thing that distinguishes them, and a
  // guard placed after the validators would arrive too late to use it.
  //
  // ⚠ WRITTEN AGAINST THE CLASS, NOT AGAINST CONSULTANT. The reported bug was a
  // consultant; the identical bug was already live for Advocate (`aow.ts` mints
  // real identities) and had simply never been triggered, because that dashboard
  // does not exist yet. A `role === 'consultant'` check would have closed the
  // report and left the defect. Any non-empty `personalRoles` fires this.
  //
  // ⭐ PARTNER IS NOT IN THE CLASS AND DOES NOT NEED TO BE — verified, not
  // assumed. D-A4 makes partner standing a property of the ACCOUNT, so every
  // partner is an account member and has real account state; they never reach the
  // no-account branch. Confirmed live 10 Aug 2026 against both live Cybercraft
  // partner identities.
  const personalRole = ctx.personalRoles?.[0];
  if (personalRole) {
    return { kind: 'personal-role-home', role: personalRole };
  }

  // A provisioned BETA account is never a SOW candidate — ahead of every other
  // branch so nothing below can drag a beta participant into the paid journey
  // (BOW writes its own states on a different population entirely).
  if (acct.provisioned && acct.isBeta) {
    return { kind: 'dashboard', reason: 'beta' };
  }

  // TERMINAL LIFECYCLE → DASHBOARD.
  //
  // ⭐ THIS IS NOT `setupState` CREEPING BACK IN AS THE SOURCE OF TRUTH — THE
  // ASYMMETRY IS THE ENTIRE POINT. `setupState` may say "finished" (→ dashboard);
  // it may NEVER say "unfinished" (→ wizard). Only one of those directions can
  // hurt anyone: the defect this build exists to kill was `awaiting_payment`,
  // stamped at account CREATION, claiming a subscriber was mid-payment and
  // forcing them into a wizard. A state that can only ever route someone AWAY
  // from a wizard cannot reproduce that.
  //
  // ⚠ AND IT IS LOAD-BEARING, NOT BELT-AND-BRACES. A long-settled account can
  // legitimately fail a validator — a subscriber who removed their last domain, a
  // manually-provisioned account, an account onboarded before these validators
  // existed. Re-litigating step evidence on such an account would drag a paying
  // customer back into the sign-up wizard years later, which is precisely the
  // reported defect wearing the new model's clothes.
  if (acct.provisioned && acct.setupState && SETUP_DONE.has(acct.setupState)) {
    return { kind: 'dashboard', reason: 'setup-complete' };
  }

  // ⛔ EVIDENCE THE SERVER DID NOT SEND IS NOT EVIDENCE OF ABSENCE.
  //
  // `domainCount` is NEW in ADR-107, and it is the sole evidence for the
  // `domain-verify` validator. A portal built after this ADR can be served by a
  // Lambda deployed before it (the Pages publish and the CDK deploy are separate
  // steps of the same pipeline, so the window is short but real) — and that
  // server omits the field. Reading `undefined` as `0` would then fail
  // `domain-verify` for EVERY provisioned account on the platform and redirect
  // all of them into the sign-up wizard: a total-outage-shaped regression caused
  // purely by deploy ordering.
  //
  // This is the same lesson as `accountStateKnown` above, one field deeper: a
  // swallowed/absent read and a genuine zero are the same bytes unless something
  // carries the distinction. `domainCount` is optional in the type FOR THIS
  // REASON — absent means "this server cannot tell me", and the safe answer to
  // that is the dashboard, never a wizard.
  if (acct.provisioned && acct.domainCount === undefined) {
    return { kind: 'dashboard', reason: 'state-unknown' };
  }

  // ⭐ THE ONE QUESTION. Which required step is not actually done?
  //
  // This single call replaces the whole setupState → requiredAction → lastObwStep
  // → ?plan= ladder documented above. `openAt` is simultaneously the resume point,
  // the entry point and the completion gate (ADR-107 §2.2), so those three can no
  // longer disagree with one another — which is what every reported "loop" was.
  const evidence = evidenceFromAccountState(acct);
  const signUp = JOURNEY_MATRIX['sign-up'];
  const openAt = firstIncompleteRequiredStep(signUp.steps, signUp.required, evidence);

  if (!openAt) {
    // Every required validator passes. An account that has genuinely finished
    // setup lands on its dashboard whether or not any flag agrees.
    return {
      kind: 'dashboard',
      reason: acct.provisioned ? 'setup-complete' : 'provisioned',
    };
  }

  // Something required is genuinely not done — run the journey and open there.
  //
  // ⚠ THE ACCOUNT MAY NOT EXIST YET, and that is a normal case, not an error: a
  // fresh self-serve visitor has no Account record, so every validator fails and
  // the journey opens at its first required step (`domain-verify`) — which is
  // precisely where a fresh sign-up belongs, and precisely where the old ladder
  // was NOT sending them.
  return { kind: 'journey', journeyId: 'sign-up', openAt };
}
